import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import { get } from 'lodash';
import useDimensions from 'react-use-dimensions';
import { createSelector } from 'reselect';
import ReactToPrint from 'react-to-print';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Button } from '@mui/material';

import { Star } from '../../parcels';
import { ReportMap } from './dynamic-components/report-map';
import { PrintMap } from './dynamic-components/print-map';
import { Actions } from './actions';
import { TRANSFORMS } from './units';
import { DataUpdateInfo } from './data-update-info';

import {
  Title,
  Row,
  RowWhite,
  ColumnHalf,
  Heading,
  Container,
  FieldTitle,
  SectionTitle,
  Subtitle,
  MapLegend,
  Square,
  FullWidth,
  Spacer,
  BottomRow,
  Logo,
} from './ui';

import { ERROR, CONTRAST } from '../../theme';
import { StaticMap } from './static-map';
import { LineGraphs } from './line-graphs';
import { ReportInput } from './dynamic-components/report-input';
import { PieChartElement } from './dynamic-components/pie-chart-element';
import { Meter } from '../../graphs';
import { ComparisonLineGraphs } from './comparison-line-graphs';
import { CensusComparisonTable } from './dynamic-components/census-comparison-table';

const mapState = createSelector(
  ({ sidePanel }) => sidePanel,
  ({ reportContents, selectedReport }) => reportContents[selectedReport],
  ({ reportLoading }) => reportLoading,
  ({ city }) => city,
  ({ defaultZipcode }) => defaultZipcode,
  ({ state }) => state,
  (sidePanel, reportContents, reportLoading, city, defaultZipcode, state) => ({
    sidePanel,
    reportContents,
    reportLoading,
    city,
    defaultZipcode,
    state,
  }),
);

let widthCache = 0;
export const DynamicModal = () => {
  const {
    reportContents,
    reportLoading,
    city,
    defaultZipcode,
    state,
  } = useSelector(mapState);

  if (reportLoading || !reportContents) {
    return (
      <Container>
        <Row>One moment...</Row>
      </Container>
    );
  }

  return (
    <Dynamic
      reportContents={reportContents}
      reportLoading={reportLoading}
      city={city}
      defaultZipcode={defaultZipcode}
      state={state}
    />
  );
};

export const PrintMapModal = () => {
  const componentRef = useRef();

  return (
    <Container>
      <ReactToPrint
        trigger={() => <Button>Print</Button>}
        content={() => componentRef.current}
      />
      <Container ref={componentRef}>
        <Row>
          <PrintMap />
        </Row>
        <BottomRow>
          <div>
            <small>
              <strong>Neighborhood Intel</strong>
              &nbsp;by
            </small>
          </div>
          <Logo src="/dynamo-primary-logo.png" alt="Dynamo Metrics" />
        </BottomRow>
      </Container>
    </Container>
  );
};

const areStarsAllowed = ({ sidebarFlags }) => ({
  allowStars: sidebarFlags.indexOf('STARRED_PARCELS') > -1,
});

export const Dynamic = ({
  reportContents,
  reportLoading,
  city,
  limited = false,
}) => {
  const { allowStars } = useSelector(areStarsAllowed);
  const componentRef = useRef();
  const [widthRef, { width }] = useDimensions();

  widthCache = width || widthCache;

  if (reportLoading || !reportContents) {
    return (
      <Container>
        <Row>One moment...</Row>
      </Container>
    );
  }

  const sections = reportContents.report.sections || [];
  const identifier = reportContents.report.identifier || 'parcel_id';

  const tableHandle = {};

  return (
    <Container ref={componentRef}>
      <Row ref={widthRef}>
        {sections.map((section, i) => {
          const { properties } = section;
          const rowElements = properties.map((prop) => {
            if (limited && prop.excludeFromLimited) return null;

            switch (prop.type) {
              case 'HIDDEN':
                return null;
              case 'ACTIONS': {
                if (limited) return null;
                let pointString = '';
                if (reportContents && reportContents.parcels.length) {
                  pointString = reportContents.parcels[0].point
                    .replace('POINT(', '')
                    .replace(')', '')
                    .split(' ')
                    .reverse()
                    .join(',');
                }

                return (
                  <ColumnHalf key="download">
                    <Actions
                      componentRef={componentRef}
                      parcels={[reportContents.parcels.map((p) => p[identifier])]}
                      filterString={prop.filterString || reportContents.filterString}
                      options={prop.options}
                      props={prop.props}
                      pointString={pointString}
                    />
                  </ColumnHalf>
                );
              }
              case 'UPDATE_FREQUENCY': {
                return <DataUpdateInfo key="Update Frequency" />;
              }
              case 'DOLLARS':
              case 'PERCENT':
              case 'PERCENT_DECIMAL':
              case 'TEXT_MAP':
              case 'DATE':
              case 'NUMBER':
              case 'NUMBER_DECIMAL':
              case 'SMALL_NUMBER':
              case 'TEXT': {
                let value = null;
                if (reportContents.parcels.length) {
                  const key = prop.source
                    ? `${prop.source}:${prop.column}`
                    : prop.column;
                  value = reportContents.parcels[0][key];

                  if (prop.localMethod === 'divide') {
                    const data = reportContents.parcels[0];
                    const { input } = prop;

                    value = data[input[0]] / data[input[0]];
                  }
                } else if (reportContents.data) {
                  const key = prop.source
                    ? `${prop.method}:${prop.source}:${prop.column}`
                    : `${prop.method}:${prop.column}`;
                  value = reportContents.data[key];

                  if (prop.localMethod === 'divide') {
                    const { data } = reportContents;
                    const { input } = prop;

                    value = data[input[0]] / data[input[1]];
                  }
                }

                if (value == null && !prop.default) return null;
                return (
                  <Row key={`${prop.column || i}-${prop.label}-${value}`}>
                    <ColumnHalf>
                      <FieldTitle>{prop.label}</FieldTitle>
                    </ColumnHalf>
                    <ColumnHalf>
                      {value != null
                        ? TRANSFORMS[prop.type](value, prop.map) + (prop.postpend || '')
                        : prop.default}
                    </ColumnHalf>
                  </Row>
                );
              }
              case 'TEXT_TABLE': {
                const fields = reportContents.parcels[0][prop.endpoint];

                if (!tableHandle[prop.endpoint]) {
                  tableHandle[prop.endpoint] = {
                    columns: [],
                    data: [],
                    widths: [],
                  };
                }

                if (!fields && prop.last) return <FieldTitle key={`${prop.endpoint}-no-key`}>No records</FieldTitle>;
                if (!fields) return null;

                tableHandle[prop.endpoint].widths.push(prop.width);

                fields.forEach((field, fieldIndex) => {
                  let value = null;
                  const key = prop.source
                    ? `${prop.source}:${prop.column}`
                    : prop.column;
                  value = field[key];

                  if (tableHandle[prop.endpoint].columns.indexOf(prop.label) === -1) {
                    tableHandle[prop.endpoint].columns.push(prop.label);
                  }

                  if (!tableHandle[prop.endpoint].data[fieldIndex]) {
                    tableHandle[prop.endpoint].data.push({});
                  }

                  tableHandle[prop.endpoint]
                    .data[fieldIndex][prop.label] = (value != null && prop.unit) ?
                      TRANSFORMS[prop.unit](value, prop.map) + (prop.postpend || '') : value;
                });

                if (!prop.last) return null;

                return (
                  <RowWhite
                    key={`title-${prop.endpoint || i}-${prop.label}`}
                    style={{
                      height: 252,
                      maxHeight: 600,
                      position: 'relative',
                      flexDirection: 'column',
                    }}
                  >
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow style={{ textTransform: 'capitalize' }}>
                            {tableHandle[prop.endpoint].columns.map((col, colIndex) => (
                              <TableCell
                                // eslint-disable-next-line react/no-array-index-key
                                key={`column-${colIndex}`}
                                style={{ minWidth: tableHandle[prop.endpoint].widths[colIndex] || 'auto' }}
                              >
                                <b>{col}</b>
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {
                            tableHandle[prop.endpoint].data.map((data, rowIndex) => (
                              // eslint-disable-next-line react/no-array-index-key
                              <TableRow key={`row-${rowIndex}-${prop.label}`}>
                                {
                                  tableHandle[prop.endpoint].columns.map((col, colIndex) => (
                                    <TableCell
                                      scope="row"
                                      // eslint-disable-next-line react/no-array-index-key
                                      key={`row-${rowIndex}-cell-${col}`}
                                      style={{ minWidth: tableHandle[prop.endpoint].widths[colIndex] || 'auto' }}
                                    >
                                      {data[col] || (<i>empty</i>)}
                                    </TableCell>
                                  ))
                                }
                              </TableRow>
                            ))
                          }
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </RowWhite>
                );
              }
              case 'IMAGE': {
                let value = null;
                if (reportContents.parcels.length) {
                  const key = prop.source
                    ? `${prop.source}:${prop.column}`
                    : prop.column;
                  value = reportContents.parcels[0][key];
                } else if (reportContents.data) {
                  const key = prop.source
                    ? `${prop.method}:${prop.source}:${prop.column}`
                    : `${prop.method}:${prop.column}`;
                  value = reportContents.data[key];
                }

                if (value == null && !prop.default) return null;
                return (
                  <RowWhite key={`${prop.column || i}-${prop.label}-${value}`}>
                    <ColumnHalf>
                      <img
                        src={value}
                        alt={prop.labels}
                        style={{
                          paddingTop: 16,
                          paddingBottom: 16,
                          height: prop.height || 'auto',
                          width: prop.width || 'auto',
                        }}
                      />
                    </ColumnHalf>
                  </RowWhite>
                );
              }
              case 'LINK': {
                let value = null;

                if (prop.value) {
                  value = prop.value;
                } else if (reportContents.parcels.length) {
                  const key = prop.source
                    ? `${prop.source}:${prop.column}`
                    : prop.column;
                  value = reportContents.parcels[0][key];
                } else if (reportContents.data) {
                  const key = prop.source
                    ? `${prop.method}:${prop.source}:${prop.column}`
                    : `${prop.method}:${prop.column}`;
                  value = reportContents.data[key];
                }

                if (value == null && !prop.default) return null;
                return (
                  <Row key={`${prop.column || i}-${prop.label}-${value}`}>
                    <ColumnHalf>
                      <a href={value}>
                        <FieldTitle>{prop.label}</FieldTitle>
                      </a>
                    </ColumnHalf>
                  </Row>
                );
              }
              case 'TITLE': {
                return (
                  <RowWhite
                    key={`title-${prop.column}-${prop.label}`}
                    style={{
                      pageBreakBefore: prop.newPage ? 'always' : 'auto',
                    }}
                  >
                    <ColumnHalf>
                      <SectionTitle>{prop.label}</SectionTitle>
                    </ColumnHalf>
                  </RowWhite>
                );
              }
              case 'SUBTITLE': {
                return (
                  <RowWhite
                    key={`title-${prop.column}-${prop.label}`}
                    style={{
                      pageBreakBefore: prop.newPage ? 'always' : 'auto',
                    }}
                  >
                    <ColumnHalf>
                      <Subtitle>{prop.label}</Subtitle>
                    </ColumnHalf>
                  </RowWhite>
                );
              }
              case 'DATA_STREAM': {
                const dataStream = (reportContents.parcels[0].dataStreams || {})[prop.dataStreamId];
                if (!dataStream.entities.length) return null;

                const columns = Object.keys(dataStream.entities[0]).map(key => ({
                  field: key,
                  headerName: key.split('_').join(' '),
                  width: 210,
                }));
                const rows = dataStream.entities.map((entity, j) => ({ ...entity, id: j }));

                return (
                  <RowWhite
                    key={`title-${prop.dataStreamId}-${prop.label}`}
                    style={{
                      height: (rows.length * 52) + 252,
                      maxHeight: 600,
                      position: 'relative',
                      flexDirection: 'column',
                    }}
                  >
                    <SectionTitle>{prop.label}</SectionTitle>
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow style={{ textTransform: 'capitalize' }}>
                            {columns.map((col) => (<TableCell>{col.headerName}</TableCell>))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {
                            rows.map((row, idx) => (
                              // eslint-disable-next-line react/no-array-index-key
                              <TableRow key={idx}>
                                {
                                  columns.map((col, innerIdx) => (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <TableCell scope="row" key={`cell-${innerIdx}-${row[col.field]}`}>
                                      {row[col.field] || (<i>empty</i>)}
                                    </TableCell>
                                  ))
                                }
                              </TableRow>
                            ))
                          }
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </RowWhite>
                );
              }
              case 'FAKE_BUTTON': {
                return (
                  <RowWhite key={`fake-button-${prop.label}`} style={{ paddingBottom: 12, cursor: 'default' }}>
                    <Button inactive="true" variant="contained">{prop.label}</Button>
                  </RowWhite>
                );
              }
              case 'CENSUS_COMPARISON_TABLE': {
                const censusTable = get(reportContents, `censusTables[${prop.table}]`, []);
                if (!censusTable.length) return null;

                return (
                  <RowWhite key={`census-comparison-table-${prop.table}`} style={{ paddingBottom: 24 }}>
                    <CensusComparisonTable censusTable={censusTable} prepend={prop.prepend} />
                  </RowWhite>
                );
              }
              case 'INPUT': {
                return <ReportInput key={`input-${prop.id}-${prop.label}`} prop={prop} reportContents={reportContents} />;
              }
              case 'LARA_LINK': {
                const ownerName = reportContents.parcels[0].owner_name;
                const onClick = () => {
                  const copyText = document.createElement('input');
                  copyText.value = ownerName;

                  document.body.appendChild(copyText);
                  copyText.select();
                  document.execCommand('copy');

                  window.open(
                    'https://cofs.lara.state.mi.us/corpweb/CorpSearch/CorpSearch.aspx',
                  );
                  copyText.remove();
                };

                return (
                  <Row key="LARA_LINK">
                    <ColumnHalf>
                      <FieldTitle>{prop.label}</FieldTitle>
                    </ColumnHalf>
                    <ColumnHalf>
                      <div
                        role="link"
                        tabIndex={0}
                        style={{ color: 'blue', textDecoration: 'underline', cursor: 'pointer' }}
                        onClick={onClick}
                        onKeyPress={onClick}
                      >
                        Copy to clipboard and open:&nbsp;
                        {ownerName}
                      </div>
                    </ColumnHalf>
                  </Row>
                );
              }
              case 'MAP': {
                let pointString = '';
                if (reportContents && reportContents.parcels.length) {
                  pointString = reportContents.parcels[0].point
                    .replace('POINT(', '')
                    .replace(')', '')
                    .split(' ')
                    .reverse()
                    .join(',');
                }

                if (limited && !prop.options.includes('HIDE_STREETVIEW')) {
                  return (
                    <StaticMap
                      key={`${prop.column}-${prop.label}`}
                      widthCache={widthCache * 2}
                      pointString={pointString}
                    />
                  );
                }

                return [
                  <RowWhite key={`${prop.column}-${prop.label}`}>
                    <ReportMap
                      source={reportContents.report.source}
                      identifier={identifier}
                      parcels={[
                        reportContents.parcels.map(
                          (parcel) => parcel[identifier],
                        ),
                      ]}
                      layer={reportContents.layer}
                      filterString={reportContents.filterString}
                      properties={prop}
                      padding={prop.padding}
                      radius={prop.radius}
                      bbox={reportContents.bbox}
                      style={reportContents.style}
                      showRadius={(prop.options || []).includes('RADIUS')}
                      singleParcel={
                        (prop.options || []).includes('STREETVIEW') ||
                        (prop.options || []).includes('SINGLE_PARCEL')
                      }
                      halfWidth={
                        (prop.options || []).includes('STREETVIEW') &&
                        reportContents.point
                      }
                    />
                    {(prop.options || []).includes('STREETVIEW') &&
                      reportContents &&
                      reportContents.point ? (
                      <StaticMap
                        widthCache={widthCache}
                        pointString={pointString}
                      />
                    ) : null}
                  </RowWhite>,
                  <MapLegend key="map-legend">
                    {(prop.options || []).includes('STREETVIEW') &&
                      reportContents.point
                      ? [
                        <Title key="title">Key</Title>,
                        <RowWhite key="row">
                          <Square color={ERROR} />
                          <span>Parcel</span>
                          {
                            (prop.options || []).includes('RADIUS')
                              ? (
                                <>
                                  <Square color={CONTRAST} />
                                  <span>Parcels within {prop.radius || 500}ft</span>
                                </>
                              )
                              : null

                          }
                        </RowWhite>,
                      ]
                      : null}
                  </MapLegend>,
                ];
              }
              case 'PIE_GRAPH': {
                if (limited) return null;
                return (
                  <PieChartElement
                    key={`${prop.column}-${prop.label}`}
                    city={city}
                    reportContents={reportContents}
                    prop={prop}
                  />
                );
              }
              case 'METER': {
                let value = null;
                if (reportContents.parcels.length) {
                  const key = prop.source
                    ? `${prop.source}:${prop.column}`
                    : prop.column;
                  value = reportContents.parcels[0][key];
                } else if (reportContents.data) {
                  const key = prop.source
                    ? `${prop.method}:${prop.source}:${prop.column}`
                    : `${prop.method}:${prop.column}`;
                  value = reportContents.data[key];
                }
                if (value == null && !prop.default) return null;
                return (
                  <div
                    key={`${prop.column}-${prop.title}`}
                  >
                    <SectionTitle>{prop.title}</SectionTitle>
                    <Meter
                      prop={prop}
                      reportContents={reportContents}
                      total={prop.total}
                      value={value}
                    />
                  </div>
                );
              }
              case 'COMPARISON_LINE_GRAPH': {
                if (limited) return null;

                return (
                  <ComparisonLineGraphs
                    key={`${prop.graph}-${prop.title}`}
                    prop={prop}
                    reportContents={reportContents}
                    city={city}
                  />
                );
              }
              case 'LINE_GRAPH': {
                if (limited) return null;
                return (
                  <LineGraphs
                    key={`${prop.graph}-${prop.title}`}
                    prop={prop}
                    reportContents={reportContents}
                    city={city}
                  />
                );
              }
              default:
                return null;
            }
          });
          return (
            <FullWidth key={section.title}>
              <Row key="HEADER">
                <Heading>
                  {get(reportContents, section.titleSelector, section.title)}
                </Heading>
                <Spacer />
                {reportContents.parcels.length === 1 && allowStars ? (
                  <Star parcelId={reportContents.parcels[0][identifier]} />
                ) : null}
              </Row>
              {rowElements}
            </FullWidth>
          );
        })}
      </Row>
      <BottomRow>
        <div>
          <small>
            <strong>Neighborhood Intel</strong>
            &nbsp;by
          </small>
        </div>
        <Logo src="/dynamo-primary-logo.png" alt="Dynamo Metrics" />
      </BottomRow>
    </Container>
  );
};
